// PDSubscriptionsGiftToMe.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faGift } from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../hooks/useFetch";
import Loader from "../../components/utils/Loader";

const PDSubscriptionsGiftToMe = () => {
    const authState = useSelector((state) => state.authReducer);

    const recipientCode = authState?.user?.think5whyscode;

    const [fetchData, { loading }] = useFetch();

    const [giftedToMe, setGiftedToMe] = useState([]);
    const [selectedGiftId, setSelectedGiftId] = useState(null);

    const fetchGiftedToMeList = useCallback(() => {
        if (!recipientCode) return;

        const config = {
            url: "/pdgiftcoupon/byreceiver",
            method: "post",
            headers: {
                Authorization: authState.token,
            },
            data: {
                recipientCode,
            },
        };

        fetchData(config, { showSuccessToast: false })
            .then((response) => {
                if (response?.giftCoupons) {
                    setGiftedToMe(response.giftCoupons);
                } else {
                    setGiftedToMe([]);
                }
            })
            .catch((error) => {
                console.error("Error fetching received gifts:", error);
                setGiftedToMe([]);
            });
    }, [authState.token, fetchData, recipientCode]);

    useEffect(() => {
        fetchGiftedToMeList();
    }, [fetchGiftedToMeList]);

    const toggleGiftDetails = (giftId) => {
        setSelectedGiftId((prev) => (prev === giftId ? null : giftId));
    };

    const sortedGiftedToMe = useMemo(() => {
        return [...giftedToMe].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
    }, [giftedToMe]);

    const formatDateTime = (date) => {
        if (!date) return "-";

        const d = new Date(date);

        if (isNaN(d.getTime())) return "-";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        const hours = String(d.getHours()).padStart(2, "0");
        const mins = String(d.getMinutes()).padStart(2, "0");

        return `${day}-${month}-${year} ${hours}:${mins}`;
    };

    return (
        <div className="w-full">

            {/* HEADER */}
            <div className="rounded-3xl bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 p-6 shadow-xl">
                <div className="flex items-center gap-4">

                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
                        🎉
                    </div>

                    <div>
                        <h2 className="text-2xl font-bold text-white">
                            Gifts Received
                        </h2>

                        <p className="mt-1 text-xs text-cyan-100">
                            Someone thought of you 💜
                        </p>
                    </div>

                </div>
            </div>

            {/* BODY */}
            <div className="mt-4 w-full">
                <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-4 backdrop-blur-xl">

                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            {sortedGiftedToMe.length === 0 ? (

                                <div className="flex h-72 flex-col items-center justify-center gap-3">

                                    <div className="text-6xl">
                                        🎁
                                    </div>

                                    <h3 className="text-lg font-semibold text-white">
                                        No Gifts Yet
                                    </h3>

                                    <p className="max-w-sm text-center text-sm text-slate-400">
                                        When someone gifts you a PurpleDoor
                                        subscription, it will appear here.
                                    </p>

                                </div>

                            ) : (

                                sortedGiftedToMe.map((gift, index) => (

                                    <div
                                        key={gift._id}
                                        className="
                                            w-full
                                            my-4
                                            rounded-3xl
                                            border
                                            border-cyan-500/20
                                            bg-gradient-to-r
                                            from-slate-900
                                            via-cyan-950/30
                                            to-purple-950/30
                                            p-5
                                            shadow-xl
                                        "
                                    >

                                        {/* GRATITUDE BANNER */}
                                        <div className="mb-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-3">
                                            <div className="text-xs text-cyan-200">
                                                💜{" "}
                                                <strong>{gift.gifterName}</strong>{" "}
                                                gifted you a{" "}
                                                <strong>{gift.product}</strong>{" "}
                                                subscription.
                                            </div>
                                        </div>

                                        {/* TOP ROW */}
                                        <div className="flex items-start justify-between gap-3">

                                            <div className="flex-1">

                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">
                                                        🎁
                                                    </span>

                                                    <span className="font-bold text-white">
                                                        {gift.product}
                                                    </span>
                                                </div>

                                                <div className="mt-2 flex flex-wrap items-center gap-2">

                                                    <span className="rounded-full bg-cyan-500/20 px-3 py-1 text-xs text-cyan-300">
                                                        🎁 Gift Received
                                                    </span>

                                                    <span className="rounded-full bg-purple-500/20 px-3 py-1 text-xs text-purple-300">
                                                        👤 {gift.gifterCode}
                                                    </span>

                                                    {gift.redeemedAt ? (
                                                        <span className="rounded-full bg-green-500/20 px-3 py-1 text-xs text-green-300">
                                                            ✅ Redeemed
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-yellow-500/20 px-3 py-1 text-xs text-yellow-300">
                                                            🎟️ Ready To Redeem
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="mt-3 text-xs text-slate-400">
                                                    Gift #{index + 1} • Received on{" "}
                                                    {formatDateTime(
                                                        gift.createdAt
                                                    )}
                                                </div>

                                            </div>

                                            <button
                                                onClick={() =>
                                                    toggleGiftDetails(gift._id)
                                                }
                                                className="
                                                    flex
                                                    h-10
                                                    w-10
                                                    items-center
                                                    justify-center
                                                    rounded-xl
                                                    border
                                                    border-slate-700
                                                    bg-slate-800
                                                    text-cyan-300
                                                    transition-all
                                                    hover:border-cyan-500
                                                    hover:bg-cyan-500/10
                                                "
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>

                                        </div>

                                        {/* DETAILS */}
                                        {selectedGiftId === gift._id && (

                                            <div className="mt-5 rounded-3xl border border-cyan-500/20 bg-slate-950/70 p-5">

                                                <div className="flex items-center gap-2 mb-4">
                                                    <FontAwesomeIcon
                                                        icon={faGift}
                                                        className="text-cyan-300"
                                                    />

                                                    <h3 className="text-lg font-bold text-white">
                                                        Gift Details
                                                    </h3>
                                                </div>

                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Gifted By
                                                        </div>

                                                        <div className="mt-1 font-semibold text-white">
                                                            👤 {gift.gifterName}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Think5Whys Code
                                                        </div>

                                                        <div className="mt-1 font-semibold text-cyan-300">
                                                            {gift.gifterCode}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Coupon Code
                                                        </div>

                                                        <div className="mt-1 break-all font-mono text-purple-300">
                                                            🎟️ {gift.couponCode}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Gift Value
                                                        </div>

                                                        <div className="mt-1 font-semibold text-green-300">
                                                            💰 ₹{" "}
                                                            {Number(
                                                                gift.giftValue || 0
                                                            ).toFixed(2)}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Product
                                                        </div>

                                                        <div className="mt-1 font-medium text-white">
                                                            🚀 {gift.product}
                                                        </div>
                                                    </div>

                                                    <div className="rounded-2xl bg-white/5 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            Received On
                                                        </div>

                                                        <div className="mt-1 font-medium text-white">
                                                            📅{" "}
                                                            {formatDateTime(
                                                                gift.createdAt
                                                            )}
                                                        </div>
                                                    </div>

                                                </div>

                                                {/* MESSAGE */}
                                                {gift.giftMessage && (

                                                    <div className="mt-4 rounded-2xl border border-pink-500/20 bg-pink-500/10 p-4">

                                                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-pink-300">
                                                            💌 Personal Message
                                                        </div>

                                                        <div className="italic leading-relaxed text-pink-100">
                                                            "{gift.giftMessage}"
                                                        </div>

                                                    </div>

                                                )}

                                                {/* STATUS */}
                                                <div className="mt-4 rounded-2xl bg-white/5 p-4">

                                                    <div className="mb-2 text-[10px] uppercase tracking-wider text-slate-500">
                                                        Subscription Status
                                                    </div>

                                                    {gift.redeemedAt ? (
                                                        <>
                                                            <div className="font-semibold text-green-300">
                                                                🎉 Subscription Activated
                                                            </div>

                                                            <div className="mt-1 text-sm text-slate-300">
                                                                Redeemed on{" "}
                                                                {formatDateTime(
                                                                    gift.redeemedAt
                                                                )}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="font-semibold text-yellow-300">
                                                                🎟️ Gift Waiting To Be Redeemed
                                                            </div>

                                                            <div className="mt-1 text-sm text-slate-400">
                                                                Redeem this gift
                                                                coupon to activate
                                                                your subscription.
                                                            </div>
                                                        </>
                                                    )}

                                                </div>

                                            </div>

                                        )}

                                    </div>

                                ))

                            )}
                        </>
                    )}

                </div>
            </div>

        </div>
    );
};

export default PDSubscriptionsGiftToMe;